import { Box } from '@mui/material';
import type { GridCellParams, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { StyleObj } from '../../@types';
import type { BettingSlipWithMonitoringInfo } from '../../@types/api';
import { useAudio } from '../../contexts/AudioContext';
import { defaultColumnsTicketAuthorization } from '../../helpers/table';
import { usePagination } from '../../hooks/usePagination';
import useSort from '../../hooks/useSort';
import usePendingTicketsWindow from '../../hooks/usePendingTicketsWindow';
import { createColumn } from '../../utils';
import { BettingSlipFromIcon } from '../icons';
import TicketAuthDetailsTable from '../organisms/TicketAuthDetailsTable';
import TableTemplate from '../templates/TableTemplate';
import MobileDialogWrapper from '../organisms/MobileDialogWrapper';
import isEmpty from 'lodash-es/isEmpty';
import { createNumberColumn } from '../../helpers/columns';

const styles: StyleObj = {
  container: {
    display: 'flex',
    gap: 3,
    position: 'relative',
    height: 'calc(100% - 140px)',
  },
};

const columns: GridColDef[] = [
  createColumn('ticketId', 'ID', {
    sortable: false,
    alwaysVisible: true,
    flex: 1.5,
  }),
  createColumn('createdAt', 'Created At', {
    valueGetter: (value, row, column, apiRef) => {
      return dayjs(row.createdAt).format('DD-MM-YYYY HH:mm');
    },
    alwaysVisible: true,
    flex: 1.5,
  }),
  createNumberColumn('totalStakeAmount', 'Stake', {
    alwaysVisible: true,
    flex: 0.75,
    sortable: false,
  }),
  createNumberColumn('totalOdds', 'Odds', {
    flex: 0.75,
    sortable: false,
  }),
  createNumberColumn('possibleWinnings', 'Possible Winnings', {
    alwaysVisible: true,
    flex: 1.5,
    sortable: false,
  }),
  createColumn('betsCount', 'Bets', {
    alwaysVisible: true,
    flex: 0.75,
    sortable: false,
  }),
  createColumn('from', 'From', {
    renderCell: (params: GridRenderCellParams<BettingSlipWithMonitoringInfo>) => {
      return <BettingSlipFromIcon from={params.row.from} />;
    },
    flex: 0.75,
    sortable: false,
  }),
  createColumn('user', 'Username', {
    valueGetter: (value, row, column, apiRef) => row.user?.username,
    flex: 1.5,
    sortable: false,
  }),
  createColumn('username', 'Punter Name', {
    valueGetter: (value, row, column, apiRef) => {
      return row.user?.fullName;
    },
    alwaysVisible: true,
    sortable: false,
  }),
  createColumn('sourceType', 'Source Type', {
    valueGetter: (value, row, column, apiRef) => {
      return 'Punter';
    },
    sortable: false,
  }),
];

const TicketAuthorizationPage = () => {
  const [selectedRow, setSelectedRow] = useState<string | null>(null);

  const hideAuthDetailsTable = () => {
    setSelectedRow(null);
  };

  const pendingTicketsWindow = usePendingTicketsWindow();

  const {
    data: bettingSlipsData,
    updateQueryParams,
    isLoading,
    changeQuery,
  } = usePagination<BettingSlipWithMonitoringInfo>(
    '/betting-slips',
    {
      page: 1,
      limit: 25,
      acceptStatus: 'pending',
      reoffered: false,
    },
    undefined,
    undefined,
    undefined,
    pendingTicketsWindow
  );

  const { stopAudio } = useAudio();

  useEffect(() => {
    if (bettingSlipsData && isEmpty(bettingSlipsData?.items)) {
      stopAudio();
    }
  }, [bettingSlipsData, stopAudio]);

  const { handleSort } = useSort(changeQuery);

  const handleSearch = (value: string | null) => {
    changeQuery({ search: value });
  };

  const onCellClick = (params: GridCellParams) => {
    setSelectedRow(params.row.id);
    stopAudio();
  };

  return (
    <Box sx={styles.container}>
      <TableTemplate
        rows={bettingSlipsData?.items || []}
        columns={columns}
        rowCount={bettingSlipsData?.count || 0}
        loading={isLoading}
        defaultVisibleColumns={defaultColumnsTicketAuthorization}
        handlePaginationModelChange={updateQueryParams}
        handleSearch={handleSearch}
        handleSort={handleSort}
        changeQuery={changeQuery}
        onCellClick={onCellClick}
      />
      {selectedRow && (
        <MobileDialogWrapper minWidth='180%'>
          <TicketAuthDetailsTable ticketId={selectedRow} handleClose={hideAuthDetailsTable} />
        </MobileDialogWrapper>
      )}
    </Box>
  );
};

export default TicketAuthorizationPage;
